import React, { useState, useEffect } from 'react';
import apiService from '../../services/apiService';

const inputClass = "w-full p-3 bg-white border border-slate-300 rounded-xl text-slate-800 placeholder-slate-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent";
const labelClass = "block text-slate-700 text-sm font-medium mb-1.5";
const checkboxClass = "mr-3 w-4 h-4 text-blue-600 rounded focus:ring-blue-500";

const RecordDeceasedForm = ({ preselectedMember = null, onRecordDeceased, onCancel }) => {
  const [formData, setFormData] = useState({
    memberId: preselectedMember?.id || null, familyMemberId: null,
    member: preselectedMember?.name || '', dateOfDeath: '', cause: '', burial: '', cost: '',
    funeralDate: '', paymentAuthorizedBy: '', paymentReleaseDate: '',
    reportedByName: '', reportedByRelationship: '',
    medicalCertificateReceived: false, deathRegistrationReceived: false, hospitalGpConfirmationReceived: false
  });
  const [memberOptions, setMemberOptions] = useState([]);
  const [livingFamily, setLivingFamily] = useState([]);
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [selectedNewPrimary, setSelectedNewPrimary] = useState('');
  const [loading, setLoading] = useState(true);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    const fetchOptions = async () => {
      try {
        const options = await apiService.getMembersDropdown();
        setMemberOptions(options || []);
        if (preselectedMember?.id) {
          try { const family = await apiService.getFamilyMembers(preselectedMember.id); setLivingFamily(family || []); }
          catch { setLivingFamily([]); }
        }
      } catch { setMemberOptions([]); }
      finally { setLoading(false); }
    };
    fetchOptions();
  }, [preselectedMember]);

  const displayOptions = preselectedMember
    ? memberOptions.filter(opt => (opt.type === 'member' && opt.id === preselectedMember.id) || (opt.type === 'family' && opt.parent_id === preselectedMember.id))
    : memberOptions;

  const handleChange = async (e) => {
    const { name, value, type, checked } = e.target;
    setErrors({ ...errors, [name]: '' });
    if (name === 'memberSelect') {
      if (!value) { setFormData(prev => ({ ...prev, memberId: null, familyMemberId: null })); setLivingFamily([]); return; }
      const [id, mtype] = String(value).split('-');
      if (mtype === 'member') {
        setFormData(prev => ({ ...prev, memberId: parseInt(id), familyMemberId: null, member: memberOptions.find(o => String(o.id) === String(id))?.name || '' }));
        try { const family = await apiService.getFamilyMembers(id); setLivingFamily(family || []); } catch { setLivingFamily([]); }
      } else {
        setFormData(prev => ({ ...prev, familyMemberId: parseInt(id), memberId: null }));
        setLivingFamily([]);
      }
      return;
    }
    setFormData({ ...formData, [name]: type === 'checkbox' ? checked : value });
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.memberId && !formData.familyMemberId) newErrors.member = 'Please select a person';
    if (!formData.dateOfDeath) newErrors.dateOfDeath = 'Date of death is required';
    if (!formData.reportedByName) newErrors.reportedByName = 'Reporter name is required';
    if (!formData.reportedByRelationship) newErrors.reportedByRelationship = 'Relationship is required';
    if (!formData.funeralDate) newErrors.funeralDate = 'Funeral date is required';
    if (!formData.paymentAuthorizedBy) newErrors.paymentAuthorizedBy = 'Payment authorization is required';
    if (!formData.paymentReleaseDate) newErrors.paymentReleaseDate = 'Payment release date is required';
    if (!formData.cost || parseFloat(formData.cost) <= 0) newErrors.cost = 'Estimated cost is required and must be greater than 0';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    if (formData.memberId && livingFamily.length > 0 && !showTransferModal) { setShowTransferModal(true); return; }
    onRecordDeceased({ ...formData, transferToPrimaryId: selectedNewPrimary ? parseInt(selectedNewPrimary) : null });
  };

  const relationLabel = (r) => r === 'S' ? 'Son' : r === 'D' ? 'Daughter' : r === 'W' ? 'Wife' : r === 'H' ? 'Husband' : r;

  const errClass = (field) => errors[field] ? 'border-red-400' : 'border-slate-300';

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <h2 className="text-2xl font-bold text-slate-900 mb-4">Record Deceased</h2>
      {!showTransferModal ? (
        <>
          <div>
            <label className={labelClass}>Select Person *</label>
            {loading ? (
              <div className="text-center py-4"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500 mx-auto"></div></div>
            ) : (
              <>
                <select name="memberSelect"
                  value={formData.memberId ? `${formData.memberId}-member` : formData.familyMemberId ? `${formData.familyMemberId}-family` : ''}
                  onChange={handleChange}
                  className={`${inputClass} border-${errClass('member')}`} required>
                  <option value="">Select a member or family member</option>
                  {displayOptions.map(opt => (
                    <option key={`${opt.id}-${opt.type}`} value={`${opt.id}-${opt.type}`}>{opt.name}</option>
                  ))}
                </select>
                {errors.member && <p className="text-red-500 text-sm mt-1">{errors.member}</p>}
              </>
            )}
          </div>
          <div>
            <label className={labelClass}>Date of Death *</label>
            <input type="date" name="dateOfDeath" value={formData.dateOfDeath} onChange={handleChange}
              className={inputClass} required max={new Date().toISOString().split('T')[0]} />
            {errors.dateOfDeath && <p className="text-red-500 text-sm mt-1">{errors.dateOfDeath}</p>}
          </div>
          <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 space-y-4">
            <label className="block text-slate-700 font-medium text-sm">Person Reporting Death *</label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-slate-500 text-xs mb-1">Full Name</label>
                <input type="text" name="reportedByName" value={formData.reportedByName} onChange={handleChange}
                  className={inputClass} placeholder="Name of person reporting" required maxLength={100} />
                {errors.reportedByName && <p className="text-red-500 text-sm mt-1">{errors.reportedByName}</p>}
              </div>
              <div>
                <label className="block text-slate-500 text-xs mb-1">Relationship to Deceased</label>
                <select name="reportedByRelationship" value={formData.reportedByRelationship} onChange={handleChange}
                  className={inputClass} required>
                  <option value="">Select relationship</option>
                  <option value="next_of_kin">Next of Kin</option>
                  <option value="spouse">Spouse</option>
                  <option value="son">Son</option>
                  <option value="daughter">Daughter</option>
                  <option value="sibling">Sibling</option>
                  <option value="parent">Parent</option>
                  <option value="neighbour">Neighbour</option>
                  <option value="friend">Friend</option>
                  <option value="community_member">Community Member</option>
                  <option value="imam">Imam</option>
                  <option value="hospital">Hospital/Care Home</option>
                  <option value="other">Other</option>
                </select>
                {errors.reportedByRelationship && <p className="text-red-500 text-sm mt-1">{errors.reportedByRelationship}</p>}
              </div>
            </div>
          </div>
          <div>
            <label className={labelClass}>Funeral Date *</label>
            <input type="date" name="funeralDate" value={formData.funeralDate} onChange={handleChange}
              className={inputClass} required />
            {errors.funeralDate && <p className="text-red-500 text-sm mt-1">{errors.funeralDate}</p>}
          </div>
          <div>
            <label className={labelClass}>Payment Authorized By *</label>
            <input type="text" name="paymentAuthorizedBy" value={formData.paymentAuthorizedBy} onChange={handleChange}
              className={inputClass} placeholder="Name of management member authorizing payment"
              required maxLength={100} />
            {errors.paymentAuthorizedBy && <p className="text-red-500 text-sm mt-1">{errors.paymentAuthorizedBy}</p>}
          </div>
          <div>
            <label className={labelClass}>Payment Release Date *</label>
            <input type="date" name="paymentReleaseDate" value={formData.paymentReleaseDate} onChange={handleChange}
              className={inputClass} required />
            {errors.paymentReleaseDate && <p className="text-red-500 text-sm mt-1">{errors.paymentReleaseDate}</p>}
          </div>
          <div>
            <label className={labelClass}>Documentation Received</label>
            <div className="space-y-2">
              {[
                { key: 'medicalCertificateReceived', label: 'Medical certificate of cause of death received' },
                { key: 'deathRegistrationReceived', label: 'Death registration confirmation received' },
                { key: 'hospitalGpConfirmationReceived', label: 'Hospital/GP confirmation received' },
              ].map(({ key, label }) => (
                <label key={key} className="flex items-center text-slate-600 text-sm cursor-pointer">
                  <input type="checkbox" name={key} checked={formData[key]} onChange={handleChange} className={checkboxClass} />
                  {label}
                </label>
              ))}
            </div>
          </div>
          <div>
            <label className={labelClass}>Cause of Death</label>
            <input type="text" name="cause" value={formData.cause} onChange={handleChange}
              className={inputClass} placeholder="Optional" maxLength={200} />
          </div>
          <div>
            <label className={labelClass}>Burial Details</label>
            <textarea name="burial" value={formData.burial} onChange={handleChange}
              className={inputClass} rows="3" placeholder="Details about burial or cremation arrangements"
              maxLength={1000} />
          </div>
          <div>
            <label className={labelClass}>Estimated Cost (£) *</label>
            <input type="number" name="cost" value={formData.cost} onChange={handleChange}
              className={inputClass} step="0.01" min="0.01" max="99999" placeholder="0.00" required />
            {errors.cost && <p className="text-red-500 text-sm mt-1">{errors.cost}</p>}
          </div>
          <div className="flex justify-end gap-3 pt-4 border-t border-slate-200">
            <button type="button" onClick={onCancel} className="px-5 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 font-medium rounded-xl transition-all">Cancel</button>
            <button type="submit" className="px-5 py-2.5 bg-gradient-to-r from-red-600 to-red-700 text-white font-medium rounded-xl transition-all">Continue</button>
          </div>
        </>
      ) : (
        <div className="space-y-5">
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-5">
            <h3 className="text-lg font-bold text-amber-800 mb-2">⚠️ Living Family Members Found</h3>
            <p className="text-amber-700 text-sm">This member has {livingFamily.length} living family member(s). Please select who will become the new primary member.</p>
          </div>
          <div>
            <label className={labelClass}>Select New Primary Member *</label>
            <select value={selectedNewPrimary} onChange={(e) => setSelectedNewPrimary(e.target.value)} className={inputClass} required>
              <option value="">Choose family member...</option>
              {livingFamily.map(fam => (
                <option key={fam.id} value={fam.id}>{fam.name} - {relationLabel(fam.relation)}</option>
              ))}
            </select>
          </div>
          <div className="bg-slate-50 rounded-xl p-4 border border-slate-200">
            <p className="text-slate-700 text-sm font-medium mb-2">What will happen:</p>
            <ul className="text-slate-500 text-sm space-y-1">
              <li>• Selected person becomes new primary member</li>
              <li>• Subscription transfers to new primary</li>
              <li>• Original member marked as deceased</li>
              <li>• Remaining family relationships preserved</li>
            </ul>
          </div>
          <div className="flex justify-end gap-3 pt-4 border-t border-slate-200">
            <button type="button" onClick={() => setShowTransferModal(false)} className="px-5 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 font-medium rounded-xl transition-all">Back</button>
            <button type="submit" disabled={!selectedNewPrimary}
              className="px-5 py-2.5 bg-gradient-to-r from-red-600 to-red-700 text-white font-medium rounded-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed">
              Record Deceased & Transfer
            </button>
          </div>
        </div>
      )}
    </form>
  );
};

export default RecordDeceasedForm;
